/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: inherit
 */
import React from 'react';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { formatAuditEventLabel, type AuditLogRow } from '../../shell/audit/auditLogs';
import { getAuditDetailRows } from '../../shell/audit/auditLogPresentation';
import { modalExperience } from '../../shared/modals/modalExperience.styles';
import { colors } from '../../shared/theme/colors'
import { spacing } from '../../shared/theme/spacing';
import { settingsStyles } from '../screens/settingsScreen.styles';
import { ticketColors } from '../../shared/theme/ticketColors';

type Props = {
  row: AuditLogRow | null;
  formatWhen: (iso: string) => string;
  onClose?: () => void;
  /** Panel lateral @960 — sin header de modal. */
  inline?: boolean;
  /** Dentro de tarjeta modal — sin borde duplicado. */
  embedded?: boolean;
};

export function AuditLogDetailBody({ row, formatWhen, onClose, inline = false, embedded = false }: Props) {
  const detailRows = row ? getAuditDetailRows(row) : [];

  return (
    <View style={[styles.root, inline && styles.rootInline, embedded && styles.rootEmbedded]}>
      <View style={styles.header}>
        <Text style={[settingsStyles.sectionTitle, inline && { flex: 1 }]} numberOfLines={2}>
          {row ? formatAuditEventLabel(row.event_type) : 'Detalle'}
        </Text>
        {onClose ? (
          <Pressable onPress={onClose} accessibilityRole="button" accessibilityLabel="Cerrar detalle">
            <Ionicons name={inline ? 'close-circle-outline' : 'close'} size={inline ? 22 : 24} color={ticketColors.ink} />
          </Pressable>
        ) : null}
      </View>
      {row ? (
        <>
          <Text style={modalExperience.detailMetaSecondary}>{formatWhen(row.created_at)}</Text>
          <ScrollView
            style={[styles.scroll, inline && styles.scrollInline]}
            contentContainerStyle={styles.scrollContent}
            keyboardShouldPersistTaps="handled"
          >
            <View style={modalExperience.detailMetaGrid}>
              {detailRows.map((r, i) => (
                <View key={`${r.label}-${i}`} style={modalExperience.detailMetaRow}>
                  <Text style={modalExperience.detailMetaLabel}>{r.label}</Text>
                  <Text style={modalExperience.detailMetaValue} selectable>
                    {r.value}
                  </Text>
                </View>
              ))}
            </View>
          </ScrollView>
        </>
      ) : (
        <Text style={settingsStyles.caption}>Sin datos.</Text>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    backgroundColor: ticketColors.paper,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    padding: spacing.lg,
    flex: 1,
    minHeight: 0,
  },
  rootInline: {
    borderRadius: 16,
    margin: spacing.md,
    marginLeft: 0,
  },
  rootEmbedded: {
    borderWidth: 0,
    backgroundColor: 'transparent',
    borderRadius: 0,
    padding: 0,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: spacing.sm,
    marginBottom: spacing.xs,
  },
  scroll: { maxHeight: 480 },
  scrollInline: { maxHeight: undefined, flex: 1 },
  scrollContent: { paddingBottom: spacing.md },
});
